import React, { useEffect, useState } from "react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const SHOWCASE_URL = "https://app.emergent.sh/showcase/builderfest-kevin/0b16bd18-9520-4bb0-a9e9-4e9f475e98b9";
// Vote UI disappears at midnight at the end of 20 Sep 2026 (visitor's local time)
export const VOTE_DEADLINE = new Date("2026-09-21T00:00:00");
export const voteWindowOpen = () => new Date() < VOTE_DEADLINE;

const Heart = ({ size = 14, filled }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth={filled ? 0 : 2}
    aria-hidden="true"
  >
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
  </svg>
);

/**
 * VoteHeart — one vote per IP (matching the Emergent showcase rules),
 * then opens the Builderfest showcase page so the visitor can vote there too.
 */
export const VoteHeart = ({ compact = false }) => {
  const [votes, setVotes] = useState(null);
  const [voted, setVoted] = useState(false);
  const [pulse, setPulse] = useState(false);
  const open = voteWindowOpen();

  useEffect(() => {
    if (!API_BASE || !open) return;
    fetch(`${API_BASE}/api/vote/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) { setVotes(d.votes); setVoted(d.voted); } })
      .catch(() => {});
  }, []);

  const vote = async () => {
    window.open(SHOWCASE_URL, "_blank", "noopener,noreferrer");
    toast("❤️ Almost there — tap the heart on the contest page to cast your official vote!");
    try { window.localStorage.setItem("eviq.vote_nudge_done", "1"); } catch { /* noop */ }
    if (voted) return;
    try {
      const res = await fetch(`${API_BASE}/api/vote`, { method: "POST" });
      if (res.ok) {
        const d = await res.json();
        setVotes(d.votes);
        setVoted(true);
        setPulse(true);
        setTimeout(() => setPulse(false), 600);
      }
    } catch { /* noop */ }
  };

  if (!open) return null;

  if (compact) {
    return (
      <button
        type="button"
        onClick={vote}
        data-testid="mobile-header-vote-link"
        aria-label="Vote for EVIQ in the Emergent Builderfest"
        className="p-2 inline-flex items-center gap-1"
        style={{ color: voted ? "#FF4B6E" : "#FFFFFF" }}
      >
        <span className={pulse ? "animate-ping absolute" : "hidden"}><Heart size={18} filled /></span>
        <Heart size={18} filled={voted} />
        {voted && votes != null && votes > 0 && (
          <span className="font-mono text-[10px] tabular-nums">{votes}</span>
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={vote}
      data-testid="header-vote-link"
      title={voted ? "Thanks for voting! One vote per person." : "Vote for EVIQ in the Emergent Builderfest"}
      className="inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap"
      style={
        voted
          ? {
              borderColor: "rgba(255,75,110,0.45)",
              color: "#FF4B6E",
              background: "rgba(255,75,110,0.12)",
            }
          : {
              borderColor: "rgba(255,255,255,0.35)",
              color: "#FFFFFF",
              background: "rgba(255,255,255,0.06)",
            }
      }
    >
      <Heart filled={voted} />
      {voted ? (
        <span className="font-mono text-[12px] tabular-nums" data-testid="header-vote-count">{votes ?? ""}</span>
      ) : (
        "Vote"
      )}
    </button>
  );
};
